import React, { useCallback } from 'react'
import { Stack, IconButton, Tooltip, CircularProgress } from '@material-ui/core'
import { CloudDownload, Description } from '@material-ui/icons'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import { makeStyles } from '@material-ui/styles'
import DownloadFastqButton from './DownloadFastqButton'
import DownloadBamButton from './DownloadBamButton'
import useAuth from '../../../hooks/useAuth'
import { PATH_DASHBOARD } from 'src/routes/paths'
import { Link as RouterLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import activityFill from '@iconify/icons-eva/activity-fill'
import FileNameText from '../../../components/FileNameText'

const useAIIconStyles = makeStyles((theme) => ({
  aiIconButton: {
    padding: 4,
    '& svg': {
      color: '#C58AF9',
      transition: 'all 0.2s ease',
    },
    '&:hover svg': {
      transform: 'rotate(180deg)',
      color: '#d4a4fa',
    },
  },
}))

// File names wrap at natural break points instead of being cut off: up to 480px wide (so the
// auto-sized Files table keeps room for its other columns), then over at most three lines, then
// middle-truncated with the full name in a tooltip. `break-word` with `word-break: normal` (not
// `anywhere`, which `word-break: break-word` also means) keeps the column's minimum width at the
// longest name part, so the table cannot squeeze the name to one character a line.
const FILE_NAME_SX = {
  flex: '0 1 auto',
  maxWidth: 480,
  '& > span': { overflowWrap: 'break-word', wordBreak: 'normal' },
}

const FileNameDisplay = React.memo(
  ({
    row,
    downloadingFastqId,
    downloadingReportId,
    downloadingVcfId,
    onDownloadFastq,
    onDownloadReport,
    onDownloadRawVcf,
    onOpenAIDialog,
    downloadingBamId,
    onDownloadOntBam,
    onDownloadPacBioBam,
  }) => {
    const { user, logout } = useAuth()

    const aiIconClasses = useAIIconStyles()

    const handleDownloadReport = useCallback(() => {
      onDownloadReport(row.vcf_id, row.sample_name)
    }, [onDownloadReport, row.vcf_id, row.sample_name])

    const handleDownloadRawVcf = useCallback(() => {
      onDownloadRawVcf(row.vcf_id, row.sample)
    }, [onDownloadRawVcf, row.vcf_id, row.sample])

    const handleOpenAIDialog = useCallback(() => {
      onOpenAIDialog(row.ai_annotation)
    }, [onOpenAIDialog, row.ai_annotation])

    if (!row) return null

    if (row?.file_type === 'fastq') {
      return (
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FileNameText name={row.fastq1_filename} sx={FILE_NAME_SX} />
            {user.is_superuser && (
              <Tooltip title="Go to Pipeline" arrow>
                <IconButton
                  size="small"
                  component={RouterLink}
                  to={`${PATH_DASHBOARD.pipelines}/${row.fastq1_id}/tasks`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon icon={activityFill} width={24} height={24} />
                </IconButton>
              </Tooltip>
            )}
            <DownloadFastqButton
              onClickConfirm={onDownloadFastq}
              sampleName={row.sample}
              downloadingFastqId={downloadingFastqId}
              fastqPairId={row.fastq_pair_id}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {row.fastq2_filename && <FileNameText name={row.fastq2_filename} sx={FILE_NAME_SX} />}
            {user.is_superuser && (
              <Tooltip title="Go to Pipeline" arrow>
                <IconButton
                  size="small"
                  component={RouterLink}
                  to={`${PATH_DASHBOARD.pipelines}/${row.fastq2_id}/tasks`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon icon={activityFill} width={24} height={24} />
                </IconButton>
              </Tooltip>
            )}
            {/* {row.analysis_report_exists && (
              <Tooltip title="Download Report">
                <IconButton onClick={handleDownloadReport} disabled={downloadingReportId === row.vcf_id}>
                  {downloadingReportId === row.vcf_id ? <CircularProgress size={24} /> : <Description color="error" />}
                </IconButton>
              </Tooltip>
            )} */}
          </Stack>
        </Stack>
      )
    }

    if (row?.file_type === 'ont_bam') {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <FileNameText name={row.ont_bam_filename} sx={FILE_NAME_SX} />
          {user.is_superuser && (
            <Tooltip title="Go to Pipeline" arrow>
              <IconButton
                size="small"
                component={RouterLink}
                to={`${PATH_DASHBOARD.pipelines}/${row.ont_bam_id}/tasks`}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon icon={activityFill} width={24} height={24} />
              </IconButton>
            </Tooltip>
          )}
          <DownloadBamButton
            onClickConfirm={() => onDownloadOntBam(row.ont_bam_id, row.sample)}
            sampleName={row.sample}
            downloadingBamId={downloadingBamId}
            bamId={row.ont_bam_id}
          />
        </Stack>
      )
    }

    if (row?.file_type === 'pacbio_bam') {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <FileNameText name={row.pacbio_bam_filename} sx={FILE_NAME_SX} />
          {user.is_superuser && (
            <Tooltip title="Go to Pipeline" arrow>
              <IconButton
                size="small"
                component={RouterLink}
                to={`${PATH_DASHBOARD.pipelines}/${row.pacbio_bam_id}/tasks`}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon icon={activityFill} width={24} height={24} />
              </IconButton>
            </Tooltip>
          )}
          <DownloadBamButton
            onClickConfirm={() => onDownloadPacBioBam(row.pacbio_bam_id, row.sample)}
            sampleName={row.sample}
            downloadingBamId={downloadingBamId}
            bamId={row.pacbio_bam_id}
          />
        </Stack>
      )
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <FileNameText name={row.vcf_filename} sx={FILE_NAME_SX} />
        {row?.file_type === 'vcf' && (
          <>
            <Tooltip title="Download VCF">
              <IconButton onClick={handleDownloadRawVcf} disabled={downloadingVcfId === row.vcf_id}>
                {downloadingVcfId === row.vcf_id ? <CircularProgress size={24} /> : <CloudDownload />}
              </IconButton>
            </Tooltip>
            {user.is_superuser && (
              <Tooltip title="Go to Pipeline" arrow>
                <IconButton
                  size="small"
                  component={RouterLink}
                  to={`${PATH_DASHBOARD.pipelines}/${row.vcf_id}/tasks`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon icon={activityFill} width={24} height={24} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
        {/* AI Annotation Icon */}
        {row.ai_annotation && Object.keys(row.ai_annotation).length > 0 && (
          <Tooltip title="AI annotation available">
            <IconButton
              onClick={handleOpenAIDialog}
              aria-label="Show AI annotation"
              size="small"
              className={aiIconClasses.aiIconButton}
            >
              <TipsAndUpdatesIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {row.analysis_report_exists && (
          <Tooltip title="Download Report">
            <IconButton onClick={handleDownloadReport} disabled={downloadingReportId === row.vcf_id}>
              {downloadingReportId === row.vcf_id ? <CircularProgress size={24} /> : <Description color="error" />}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    )
  },
)

FileNameDisplay.displayName = 'FileNameDisplay'

export default FileNameDisplay
